import { useRef, useState, useCallback, useEffect } from "react";
import ReactCrop, { PercentCrop } from "react-image-crop";

import { APIError, useAxios } from "../../core/_axios";
import { TemplateCrop } from "../../core/_models";
import { canvasPreviewForPercentage } from "./canvasPreview";
import { useDebounceEffect } from "./useDebounceEffect";
import { Button } from "../../routes/books/OrderBookForm";
import useLocalizedCopy from "../../hooks/useLocalizedCopy";

interface BackendCropItem {
    percent_values: PercentCrop;
    resulting_crop: {
        x: number;
        y: number;
        width: number;
        height: number;
    }
    item_is_disabled?: boolean;
}

export interface CroppableItems {
    printed_name: BackendCropItem | null;
    relationship_to_deceased: BackendCropItem | null;
    email_address: BackendCropItem | null;
    phone_number: BackendCropItem | null;
    mailing_address_line_1: BackendCropItem | null;
    mailing_address_line_2: BackendCropItem | null;
    signature_image: BackendCropItem | null;
}

function RenderRawImageIfAvailable({
    raw_input_items, item_name,
    onClickedEditSignature
}: { raw_input_items: any, item_name: string, onClickedEditSignature?: () => void }) {
    const { copy } = useLocalizedCopy();
    if (!raw_input_items) {
        return null;
    }

    const item = raw_input_items.find((item: any) => item.region === item_name);

    const actuallyOnClickedEditSignature = () => {
        // Modularizing here in-case it changes in the future
        if (onClickedEditSignature) {
            onClickedEditSignature()
        }
    }

    if (!item) {
        return <div>
            {/* TODO: Add a placeholder */}
            {onClickedEditSignature && <div>
                <div className="text-blue-500 cursor-pointer" onClick={() => {
                    actuallyOnClickedEditSignature()
                }}>
                    {copy.Common.buttons.edit}
                </div>
            </div>}
        </div>
    }

    return (
        <>
            <div className="text-lg font-bold mt-4">
                {item.cropped_only_signed_item_url ? <div>
                    <img src={item.cropped_only_signed_item_url} alt="Signature" className="object-contain" style={{ maxWidth: '100%', maxHeight: '80px' }} />
                </div> : <div>
                    {copy.Common.messages.noData}
                </div>}
            </div>
            {onClickedEditSignature && <div>
                <div className="text-blue-500 cursor-pointer" onClick={() => {
                    actuallyOnClickedEditSignature()
                }}>
                    {copy.Common.buttons.edit}
                </div>
            </div>}
        </>
    );
}

function EditSignatureCropAreas({
    crop,
    currentCropValues,
    setCurrentCropValues
}: {
    crop: TemplateCrop,
    currentCropValues: CroppableItems,
    setCurrentCropValues: (crop: CroppableItems) => void
}) {
    const { copy } = useLocalizedCopy();

    const signature = crop.latest_signature

    const previewCanvasRef = useRef<HTMLCanvasElement>(null)

    const [selectedCropArea, setSelectedCropArea] = useState<'printed_name' | 'relationship_to_deceased' | 'email_address' | 'phone_number' | 'mailing_address_line_1' | 'mailing_address_line_2' | 'signature_image'>('printed_name')

    const [imgSrc, setImgSrc] = useState<string | undefined>(signature?.public_data?.screen_capture_url)

    const imgRef = useRef<HTMLImageElement>(null)
    const [completedCrop, setCompletedCrop] = useState<PercentCrop>(
        crop.public_data.crop_values ? crop.public_data.crop_values.printed_name?.percent_values : null
    )
    
    // Update image source when signature changes
    useEffect(() => {
        if (signature?.public_data?.screen_capture_url) {
            setImgSrc(signature.public_data.screen_capture_url);
        }
    }, [signature?.unique_identifier])
    const [scale, setScale] = useState(1)
    const [rotate, setRotate] = useState(0)

    const [currentCropItemIsDisabled, setCurrentCropItemIsDisabled] = useState(false)

    const [haveCropped, setHaveCropped] = useState(!!crop.public_data.crop_values)

    const [originalImageWidth, setOriginalImageWidth] = useState<number>(0)
    const [originalImageHeight, setOriginalImageHeight] = useState<number>(0)

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        // Get the full width and height of the image:
        const fullImageWidth = e.currentTarget.naturalWidth
        const fullImageHeight = e.currentTarget.naturalHeight
        setOriginalImageWidth(fullImageWidth)
        setOriginalImageHeight(fullImageHeight)
    }

    useDebounceEffect(
        async () => {
            if (!haveCropped) {
                return;
            }
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                console.log('setting preview to: ', completedCrop)
                // We use canvasPreview as it's much faster than imgPreview.
                canvasPreviewForPercentage(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                    rotate,
                )
            }
        },
        100,
        [haveCropped, completedCrop, scale, rotate],
    )

    return (
        <>
            <div style={{ flex: 2, position: 'relative' }}>
                {/* Left Column */}
                <div>
                    {!!imgSrc && (
                        <ReactCrop
                            crop={currentCropValues[selectedCropArea] && currentCropValues[selectedCropArea]?.percent_values ? currentCropValues[selectedCropArea]?.percent_values : undefined}
                            onChange={(pixelCrop, percentCrop) => {
                                setHaveCropped(true)
                                // We need to keep percent_values so that it renders well in the preview,
                                // And then compute a "resulting_crop" which the Lambda will use.
                                setCurrentCropValues({
                                    ...currentCropValues,
                                    [selectedCropArea]: {
                                        percent_values: percentCrop,
                                        resulting_crop: {
                                            x: originalImageWidth * percentCrop.x / 100,
                                            y: originalImageHeight * percentCrop.y / 100,
                                            width: originalImageWidth * percentCrop.width / 100,
                                            height: originalImageHeight * percentCrop.height / 100
                                        },
                                        item_is_disabled: currentCropItemIsDisabled
                                    }
                                })
                            }}
                            onComplete={(pixelCrop, percentCrop) => {
                                setCompletedCrop(percentCrop)
                            }}
                        // aspect={aspect}
                        // minWidth={400}
                        // minHeight={100}
                        // circularCrop
                        >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                ref={imgRef}
                                alt="Select crop"
                                src={imgSrc}
                                style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                                onLoad={onImageLoad}
                            />
                        </ReactCrop>
                    )}
                </div>
            </div>
            <div style={{ flex: 2, marginLeft: 16, marginRight: 16 }}>
                <div>
                    <select className="w-full mt-2 p-2 border border-gray-300 rounded-md text-black"
                        onChange={(e) => {
                            setHaveCropped(false)
                            const targetValueToCrop = e.target.value as keyof CroppableItems
                            setSelectedCropArea(targetValueToCrop)
                            // Update the preview also:
                            if (currentCropValues[targetValueToCrop] && currentCropValues[targetValueToCrop]?.percent_values) {
                                setHaveCropped(true)
                                setCompletedCrop(
                                    currentCropValues[targetValueToCrop]!.percent_values
                                )
                            } else {
                                // Clear the image:
                                previewCanvasRef.current?.getContext('2d')?.clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height)
                            }
                            setCurrentCropItemIsDisabled(!!currentCropValues[targetValueToCrop]?.item_is_disabled)
                        }}
                        value={selectedCropArea}
                    >
                        <option value="printed_name">{copy.Common.labels.name}</option>
                        <option value="relationship_to_deceased">{copy.Public.sign.messageLabel}</option>
                        <option value="phone_number">{copy.Common.labels.phone}</option>
                        <option value="email_address">{copy.Common.labels.email}</option>
                        <option value="mailing_address_line_1">{copy.Common.labels.address} (1)</option>
                        <option value="mailing_address_line_2">{copy.Common.labels.address} (2)</option>
                        <option value="signature_image">{copy.Services.signatures.viewSignature}</option>
                    </select>
                    <div>
                        {/*currentCropItemIsDisabled:*/}
                        <h4>
                            {copy.Common.labels.disabled}?
                        </h4>
                        <input
                            type="checkbox"
                            checked={currentCropItemIsDisabled}
                            onChange={(e) => {
                                setCurrentCropItemIsDisabled(e.target.checked)
                                setCurrentCropValues({
                                    ...currentCropValues,
                                    [selectedCropArea]: {
                                        ...currentCropValues[selectedCropArea]!,
                                        item_is_disabled: e.target.checked
                                    }
                                })
                            }}
                        />
                    </div>
                </div>
                <div className='mt-4'>
                    <canvas
                        ref={previewCanvasRef}
                        style={{
                            border: '1px solid black',
                            objectFit: 'contain',
                            width: 500,
                            height: 100,
                        }}
                        onClick={() => {
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export function ManageCropFormCropper({
    crop,
    setCrop,
    currentCropValues,
    setCurrentCropValues
}: {
    crop: TemplateCrop,
    setCrop: (crop: TemplateCrop) => void,
    currentCropValues: CroppableItems,
    setCurrentCropValues: (crop: CroppableItems) => void
}) {

    return (
        <div className="mt-1" style={{ display: 'flex', flexGrow: 1, maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' }}>
            <EditSignatureCropAreas
                crop={crop}
                currentCropValues={currentCropValues}
                setCurrentCropValues={setCurrentCropValues}
            />
        </div>
    );
}
